import { useEffect, useMemo, useState } from 'react';

import { QueryClient } from '@tanstack/react-query';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';

import {
  useGetProviderIntakeItemStatusesCount,
  useGetUserIntakeItemStatusesCount,
  useListSitesByProviderUser,
  usePaginateIntakeItemsByProvider,
  usePreFetchPaginateIntakeItemsByProvider,
} from 'api/resource';
import { useProviderAllSites } from 'hooks/useProviderAllSites';
import { useCurrentUserStore } from 'store/UserStore';

import { resolveCurrentUploadProviderID } from './resolveCurrentUploadProvider';
import {
  getUploadStatusGroupCounts,
  isUploadStatusesCountResult,
  UploadStatusGroupCounts,
} from './uploadStatus';

export const useProviderUploadManagementData = (queryClient: QueryClient) => {
  const { isAllSites, providersResult, siteID } = useProviderAllSites();
  const { id: userID, providerID, providers } = useCurrentUserStore();
  const selectedSiteID = Array.isArray(siteID) ? undefined : siteID;
  const resolvedProviderID = resolveCurrentUploadProviderID({
    providerID,
    providers,
    siteID: selectedSiteID,
  });

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    globalFilter: '',
    columnFilters: [] as MRT_ColumnFiltersState,
    sorting: [] as MRT_SortingState,
  });

  const [nextPage, setNextPage] = useState({
    pageIndex: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    globalFilter: pagination.globalFilter,
    columnFilters: pagination.columnFilters,
    sorting: pagination.sorting,
  });

  useEffect(() => {
    setNextPage({
      pageIndex: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      globalFilter: pagination.globalFilter,
      columnFilters: pagination.columnFilters,
      sorting: pagination.sorting,
    });
  }, [pagination, queryClient]);

  usePreFetchPaginateIntakeItemsByProvider(
    queryClient,
    isAllSites ? '' : resolvedProviderID || '',
    selectedSiteID,
    nextPage.pageIndex,
    nextPage.pageSize,
    nextPage.columnFilters,
    nextPage.globalFilter,
    nextPage.sorting
  );

  const intakeItemsResult = usePaginateIntakeItemsByProvider({
    providerID: isAllSites ? '' : resolvedProviderID || '',
    userID: isAllSites ? userID : undefined,
    siteID,
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    columnFilters: pagination.columnFilters,
    globalFilter: pagination.globalFilter,
    sorting: pagination.sorting,
  });

  const { data: taskStatusCountsData, isError: isUploadStatusCountsError } =
    useGetProviderIntakeItemStatusesCount({
      providerID: isAllSites ? '' : resolvedProviderID || '',
      siteID,
    });
  const { data: userStatusCountsData, isError: isUserStatusCountsError } =
    useGetUserIntakeItemStatusesCount({
      userID: isAllSites ? userID : '',
      siteID,
    });
  const activeStatusCountsData = isAllSites
    ? userStatusCountsData
    : taskStatusCountsData;
  const isActiveStatusCountsError = isAllSites
    ? isUserStatusCountsError
    : isUploadStatusCountsError;

  const statusGroupCounts = useMemo<UploadStatusGroupCounts>(
    () =>
      getUploadStatusGroupCounts(
        !isActiveStatusCountsError &&
          isUploadStatusesCountResult(activeStatusCountsData)
          ? activeStatusCountsData
          : undefined
      ),
    [activeStatusCountsData, isActiveStatusCountsError]
  );

  const {
    data: siteResults,
    isLoading: isLoadingSites,
    isError: isErrorSites,
    isFetching: isFetchingSites,
  } = useListSitesByProviderUser({
    userID,
  });

  return {
    items: intakeItemsResult.data?.data || [],
    total: intakeItemsResult.data?.total || 0,
    isLoading: intakeItemsResult.isLoading || isLoadingSites,
    isFetching: intakeItemsResult.isFetching || isFetchingSites,
    isError: intakeItemsResult.isError || isErrorSites,
    errorMessage:
      intakeItemsResult.error?.message || providersResult.error?.message || '',
    pagination,
    setPagination,
    statusGroupCounts,
    sites: siteResults || [],
  };
};
